import { useMutation } from "@tanstack/react-query";
import ApiErrorBanner from "components/ApiErrorBanner";
import Button from "components/ui/Button";
import Text from "components/ui/Text";
import TextInput from "components/ui/TextInput";
import { ChangeEvent, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "services/productAnalytics";
import { requestSignInLink } from "services/users/auth";
import signInStyles from "./SignIn.module.scss";
import styles from "./SignInLink.module.scss";
import { isValidEmail } from "./utils";

type Props = {
  defaultOpen?: boolean;
};

export default function SignInLinkRequest(props: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(props.defaultOpen ?? false);
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const request = useMutation({ mutationFn: requestSignInLink });

  const error = !email.trim()
    ? t("Auth.signInLink.emailRequired")
    : !isValidEmail(email)
      ? t("Auth.signInLink.emailInvalid")
      : undefined;
  const shownError = touched ? error : undefined;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    if (!error) {
      trackEvent("sign_in_link_requested");
      request.mutate({ email: email.trim() });
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  if (request.isSuccess) {
    return (
      <div className={styles.sent} role="status">
        <Text as="h3" variant="heading-4" className={styles.title}>
          {t("Auth.signInLink.sentTitle")}
        </Text>
        <Text as="p" variant="body-2" className={styles.description}>
          {t("Auth.signInLink.sentDescription", { email: request.variables.email })}
        </Text>
      </div>
    );
  }

  if (!open) {
    return (
      <Button variant="plain" className={signInStyles.link} onClick={() => setOpen(true)}>
        {t("Auth.signInLink.request")}
      </Button>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <Text as="h3" variant="heading-4" className={styles.title}>
        {t("Auth.signInLink.title")}
      </Text>
      <Text as="p" variant="body-2" className={styles.description}>
        {t("Auth.signInLink.description")}
      </Text>

      <ApiErrorBanner error={request.error} />

      <TextInput
        label={t("Auth.emailForm.emailLabel")}
        error={shownError}
        size="extra-large"
        name="email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        invalid={!!shownError}
      />

      <Button
        className={signInStyles.button}
        size="large"
        type="submit"
        loading={request.isPending}
      >
        {t("Auth.signInLink.submit")}
      </Button>
    </form>
  );
}
